import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { PageHero } from '../components/PageHero';
// Fix default marker icon paths for bundlers
const markerIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});
const OFFICE_POSITION: [number, number] = [51.5072, -0.1318]; // Carlton House Terrace, London
export function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const infoCards = [{
    icon: MapPin,
    title: 'Office Address',
    lines: ['African Union Commission', '10 Carlton House Terrace', 'London SW1Y 5AA', 'United Kingdom']
  }, {
    icon: Phone,
    title: 'Phone',
    lines: ['447916961113 (Procurement)', '447916961113 (General)']
  }, {
    icon: Mail,
    title: 'Email',
    lines: ['info@aucommissions.org', 'procurement@aucommissions.org']
  }, {
    icon: Clock,
    title: 'Office Hours',
    lines: ['Monday – Friday: 09:00 – 17:30 GMT', 'Saturday – Sunday: Closed', 'Public Holidays: Closed']
  }];
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitted(true);
  }
  return <>
      <PageHero breadcrumb="Contact" title="Contact Us" subtitle="Get in touch with the African Union Commission." data-id="element-292" />

      <section className="py-16 md:py-20 bg-gray-50" data-id="element-293">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-294">
          {/* Info cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16" data-id="element-295">
            {infoCards.map((card, index) => {
            const Icon = card.icon;
            return <div key={index} className="bg-white rounded-lg border border-gray-100 shadow-sm p-6" data-id="element-296">
                  <div className="w-12 h-12 rounded-lg bg-green-50 flex items-center justify-center mb-5" data-id="element-297">
                    <Icon size={22} className="text-au-green" data-id="element-298" />
                  </div>
                  <h3 className="text-lg font-bold text-au-navy mb-3" data-id="element-299">
                    {card.title}
                  </h3>
                  <div className="space-y-1" data-id="element-300">
                    {card.lines.map((line, i) => <p key={i} className="text-sm text-gray-600 leading-relaxed" data-id="element-301">
                        {line}
                      </p>)}
                  </div>
                </div>;
          })}
          </div>

          {/* Form + Map */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-302">
            {/* Form */}
            <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-8 md:p-10" data-id="element-303">
              <h2 className="text-2xl md:text-3xl font-serif font-bold text-au-navy mb-3" data-id="element-304">
                Send Us a Message
              </h2>
              <div className="h-1 w-16 bg-au-gold mb-8" data-id="element-305"></div>

              {submitted ? <div className="text-center py-12" data-id="element-306">
                  <h3 className="text-xl font-serif font-bold text-au-green mb-2" data-id="element-307">
                    Thank you for reaching out
                  </h3>
                  <p className="text-gray-600" data-id="element-308">
                    Our team will respond to your inquiry as soon as possible.
                  </p>
                </div> : <form onSubmit={handleSubmit} className="space-y-5" data-id="element-309">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-310">
                    <div data-id="element-311">
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-312">
                        Full Name <span className="text-au-red" data-id="element-313">*</span>
                      </label>
                      <input id="name" type="text" required placeholder="Your full name" className="w-full rounded border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-au-green focus:border-transparent" data-id="element-314" />
                    </div>
                    <div data-id="element-315">
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-316">
                        Email <span className="text-au-red" data-id="element-317">*</span>
                      </label>
                      <input id="email" type="email" required placeholder="you@email.com" className="w-full rounded border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-au-green focus:border-transparent" data-id="element-318" />
                    </div>
                  </div>
                  <div data-id="element-319">
                    <label htmlFor="organization" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-320">
                      Organization
                    </label>
                    <input id="organization" type="text" placeholder="Company or organization" className="w-full rounded border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-au-green focus:border-transparent" data-id="element-321" />
                  </div>
                  <div data-id="element-322">
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-323">
                      Subject
                    </label>
                    <input id="subject" type="text" placeholder="How can we help?" className="w-full rounded border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-au-green focus:border-transparent" data-id="element-324" />
                  </div>
                  <div data-id="element-325">
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-326">
                      Message <span className="text-au-red" data-id="element-327">*</span>
                    </label>
                    <textarea id="message" rows={5} required placeholder="Write your message..." className="w-full rounded border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-au-green focus:border-transparent resize-none" data-id="element-328" />
                  </div>
                  <button type="submit" className="w-full bg-au-green text-white px-6 py-3 rounded font-semibold hover:bg-au-navy transition-colors focus:outline-none focus:ring-2 focus:ring-au-green focus:ring-offset-2" data-id="element-329">
                    Send Message
                  </button>
                </form>}
            </div>

            {/* Map */}
            <div className="bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden min-h-[400px] lg:min-h-0" data-id="element-330">
              <MapContainer center={OFFICE_POSITION} zoom={15} scrollWheelZoom={false} className="w-full h-full min-h-[400px]" data-id="element-331">
                <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-332" />
                <Marker position={OFFICE_POSITION} icon={markerIcon} data-id="element-333">
                  <Popup data-id="element-334">African Union Commission</Popup>
                </Marker>
              </MapContainer>
            </div>
          </div>
        </div>
      </section>
    </>;
}